/**
 * RoadmapEditorModal — create/edit a roadmap item, with the detail body and
 * the comment thread hanging off the bottom.
 *
 * Everything is modal (no inline row editing). `sourceMemoryFile` is shown
 * read-only and never sent back: it's the seed's upsert key, so the panel has
 * no business rewriting it. Comments need a doc to attach to, so a brand-new
 * item shows a placeholder until it's been saved once.
 *
 * `description` is the one-line summary the grid and kanban render;
 * `detailedContent` is the long-form markdown body — originally the memory
 * file's content — and lives only here. Image attachments sit between the two
 * and, like comments, need a saved doc to hang off.
 */
import { useEffect, useMemo, useState } from 'react';
import MDEditor from '@uiw/react-md-editor';
import toast from 'react-hot-toast';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { RoadmapCommentThread } from './RoadmapCommentThread';
import { RoadmapAttachments } from './RoadmapAttachments';
import {
  OWNER_OPTIONS,
  PRIORITY_OPTIONS,
  SCOPE_OPTIONS,
  STATUS_OPTIONS,
  OWNER_LABELS,
  STATUS_LABELS,
  parseTags,
} from './roadmapMeta';
import { saveRoadmapItem } from '../../services/firebase';
import { formatAbsolute } from '../../utils/roadmapDates';
import type {
  RoadmapItem,
  RoadmapOwner,
  RoadmapPriority,
  RoadmapScope,
  RoadmapStatus,
  SaveRoadmapItemInput,
} from '../../types';

interface RoadmapEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** null ⇒ create a new item. */
  item: RoadmapItem | null;
  /** Called after a successful save so the grid can refetch. */
  onSaved: () => void;
}

export function RoadmapEditorModal({
  isOpen,
  onClose,
  item,
  onSaved,
}: RoadmapEditorModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [detailedContent, setDetailedContent] = useState('');
  const [status, setStatus] = useState<RoadmapStatus>('backlog');
  const [priority, setPriority] = useState<RoadmapPriority>('P2');
  const [owner, setOwner] = useState<RoadmapOwner>('james');
  const [scope, setScope] = useState<RoadmapScope | ''>('');
  const [tagsRaw, setTagsRaw] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset whenever the modal opens — create defaults, or hydrate from the row.
  useEffect(() => {
    if (!isOpen) return;
    if (item) {
      setTitle(item.title ?? '');
      setDescription(item.description ?? '');
      // Optional field — items seeded before v1.41.0 carry no body at all.
      setDetailedContent(item.detailedContent ?? '');
      setStatus(item.status ?? 'backlog');
      setPriority(item.priority ?? 'P2');
      setOwner(item.owner ?? 'james');
      setScope(item.estimatedScope ?? '');
      setTagsRaw((item.tags ?? []).join(', '));
    } else {
      setTitle('');
      setDescription('');
      setDetailedContent('');
      setStatus('backlog');
      setPriority('P2');
      setOwner('james');
      setScope('');
      setTagsRaw('');
    }
  }, [isOpen, item]);

  const titleTrimmed = title.trim();
  const tags = useMemo(() => parseTags(tagsRaw), [tagsRaw]);
  const saveDisabled = !titleTrimmed || saving;

  const handleSave = async () => {
    if (saveDisabled) return;

    const input: SaveRoadmapItemInput = {
      title: titleTrimmed,
      description: description.trim(),
      // Edges only — the internal markdown formatting is the content. The
      // callable trims identically, so a no-op edit round-trips unchanged.
      detailedContent: detailedContent.trim(),
      status,
      priority,
      owner,
      tags,
      estimatedScope: scope === '' ? null : scope,
      actor: 'james',
    };
    // Present id ⇒ update that doc. Absent ⇒ the callable creates one.
    // `sourceMemoryFile` is deliberately never sent: omitting it leaves the
    // seed's upsert key untouched on update, and a panel-authored item has none.
    if (item?.id) input.id = item.id;

    setSaving(true);
    try {
      const result = await saveRoadmapItem(input);
      toast.success(result?.created ? 'Project created' : 'Project updated');
      onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save roadmap item:', err);
      toast.error(
        err instanceof Error ? err.message : 'Failed to save project',
      );
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !saving && onClose()}
      title={item ? 'Edit project' : 'New project'}
      size="xl"
    >
      <div className="space-y-4">
        <div>
          <label className={labelClass}>
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Short, specific name"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            placeholder="What needs doing, and why"
            className={inputClass}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as RoadmapStatus)}
              className={inputClass}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as RoadmapPriority)}
              className={inputClass}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Owner</label>
            <select
              value={owner}
              onChange={(e) => setOwner(e.target.value as RoadmapOwner)}
              className={inputClass}
            >
              {OWNER_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {OWNER_LABELS[o]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Estimated scope</label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as RoadmapScope | '')}
              className={inputClass}
            >
              <option value="">Not sized</option>
              {SCOPE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass}>Tags</label>
          <input
            type="text"
            value={tagsRaw}
            onChange={(e) => setTagsRaw(e.target.value)}
            placeholder="comma, separated, tags"
            className={inputClass}
          />
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Long-form body. Seeded from the memory file, editable here. */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-baseline justify-between mb-1">
            <label className={labelClass}>Details</label>
            <span className="text-xs text-gray-400">
              Markdown · {detailedContent.length.toLocaleString()} characters
            </span>
          </div>
          {/*
            MDEditor picks its palette off the nearest `data-color-mode`
            ancestor and falls back to the OS preference — the admin is
            light-only, so it is pinned here rather than following the OS and
            rendering a dark slab inside a white modal.
          */}
          <div data-color-mode="light">
            <MDEditor
              value={detailedContent}
              onChange={(next) => setDetailedContent(next ?? '')}
              height={400}
              preview="edit"
              textareaProps={{
                placeholder:
                  'Long-form notes, phases, decisions — the full write-up.',
              }}
            />
          </div>
        </div>

        {/* Read-only provenance / audit */}
        {item && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs text-gray-500 border-t border-gray-200 pt-3">
            {item.sourceMemoryFile ? (
              <div>
                <span className="block font-medium text-gray-600">Source</span>
                <code className="break-all">{item.sourceMemoryFile}</code>
              </div>
            ) : (
              <div>
                <span className="block font-medium text-gray-600">Source</span>
                Authored in admin
              </div>
            )}
            <div>
              <span className="block font-medium text-gray-600">Created</span>
              {formatAbsolute(item.createdAt)}
              {item.createdBy ? ` · ${item.createdBy}` : ''}
            </div>
            <div>
              <span className="block font-medium text-gray-600">Updated</span>
              {formatAbsolute(item.updatedAt)}
            </div>
          </div>
        )}

        {/* Attachments. Like comments, they need a saved doc to hang off. */}
        {item?.id ? (
          <RoadmapAttachments roadmapId={item.id} itemTitle={item.title} />
        ) : (
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              Attachments
            </h3>
            <p className="text-sm text-gray-500 italic">
              Save the project to attach images.
            </p>
          </div>
        )}

        {/* Comments */}
        {item?.id ? (
          <RoadmapCommentThread roadmapId={item.id} />
        ) : (
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              Comments
            </h3>
            <p className="text-sm text-gray-500 italic">
              Save the project to enable comments.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saveDisabled}
            loading={saving}
          >
            {item ? 'Save changes' : 'Create project'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
